import React, { useImperativeHandle, useState, forwardRef } from 'react';
import {
  Menu, MenuItem, ListItemIcon, ListItemText, Dialog, DialogTitle, DialogContent,
  DialogActions, Button, TextField, ToggleButtonGroup, ToggleButton, Box, Typography,
  CircularProgress, Alert, Divider
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  ReceiptLong as DetailedIcon, Summarize as SummaryIcon, Print as PrintIcon,
  AccountBalance as AccountIcon, Close as CloseIcon,
} from '@mui/icons-material';
import { accountStatementService } from '../../services/accountStatement.service';
import { AccountStatementDto } from '../../types/accountStatement.types';
import config from '../../config';
import { toast } from 'react-toastify';

// نستخدم interface محلي مرن لتقبل CompanyInfo من أي صفحة (بعض الصفحات تعرّف الحقول كـ optional)
interface CompanyInfoLite {
  companyName?: string;
  companyNameAr?: string;
  logoBase64?: string | null;
  logoPath?: string | null;
  address?: string;
  phone?: string;
  email?: string;
  website?: string;
}

export interface AccountTarget {
  accountId: number;
  accountCode: string;
  accountName: string;
  accountNameAr?: string;
}

export interface AccountStatementContextMenuHandle {
  open: (event: React.MouseEvent, account: AccountTarget) => void;
}

interface Props {
  companyInfo: CompanyInfoLite | null;
  isRTL: boolean;
  minDate?: Date;
  maxDate?: Date;
  defaultFromDate?: string;
  defaultToDate?: string;
}

const toLocalISO = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const AccountStatementContextMenu = forwardRef<AccountStatementContextMenuHandle, Props>(
  ({ companyInfo, isRTL, minDate, maxDate, defaultFromDate, defaultToDate }, ref) => {
    const [menuAnchor, setMenuAnchor] = useState<{ mouseX: number; mouseY: number } | null>(null);
    const [account, setAccount] = useState<AccountTarget | null>(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [reportType, setReportType] = useState<'detailed' | 'summary'>('detailed');
    const [fromDate, setFromDate] = useState<Date | null>(defaultFromDate ? new Date(defaultFromDate) : (minDate || null));
    const [toDate, setToDate] = useState<Date | null>(defaultToDate ? new Date(defaultToDate) : (maxDate || new Date()));
    const [loading, setLoading] = useState(false);

    useImperativeHandle(ref, () => ({
      open: (event, acc) => {
        event.preventDefault();
        setAccount(acc);
        setMenuAnchor({ mouseX: event.clientX + 2, mouseY: event.clientY - 6 });
      },
    }), []);

    const t = {
      detailed: isRTL ? 'كشف حساب تفصيلي' : 'Detailed Statement',
      summary: isRTL ? 'كشف حساب مختصر' : 'Summary Statement',
      dialogTitle: isRTL ? 'إعدادات كشف الحساب' : 'Account Statement Options',
      account: isRTL ? 'الحساب' : 'Account',
      from: isRTL ? 'من تاريخ' : 'From Date',
      to: isRTL ? 'إلى تاريخ' : 'To Date',
      type: isRTL ? 'نوع التقرير' : 'Report Type',
      print: isRTL ? 'عرض وطباعة' : 'Preview & Print',
      cancel: isRTL ? 'إلغاء' : 'Cancel',
      loading: isRTL ? 'جارٍ التحميل...' : 'Loading...',
    };

    const handleMenuSelect = (type: 'detailed' | 'summary') => {
      setReportType(type);
      setMenuAnchor(null);
      setDialogOpen(true);
    };

    const handleClose = () => setDialogOpen(false);

    const handlePrint = async () => {
      if (!account || !fromDate || !toDate) return;
      setLoading(true);
      try {
        const data = await accountStatementService.getAccountStatement(
          account.accountId, null, toLocalISO(fromDate), toLocalISO(toDate)
        );
        openPrintWindow(data, reportType);
        setDialogOpen(false);
      } catch (e: any) {
        toast.error(e?.response?.data?.message || e?.message || 'Failed');
      } finally {
        setLoading(false);
      }
    };

    const openPrintWindow = (data: AccountStatementDto, type: 'detailed' | 'summary') => {
      const pw = window.open('', '_blank', 'width=1100,height=800');
      if (!pw) return;

      const fmt = (n: number) => (n || 0).toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
      const fmtDate = (s: string) => new Date(s).toLocaleDateString('en-GB');
      const periodStr = `${fmtDate(data.fromDate)} → ${fmtDate(data.toDate)}`;
      const isCredit = data.closingBalance < 0;
      const balanceClass = isCredit ? 'bal-credit' : 'bal-debit';

      let bodyRows = '';
      let totalsRow = '';

      if (type === 'detailed') {
        let runningBalance = data.openingBalance;
        const openingRow = `
          <tr class="opening-row">
            <td colspan="3" style="text-align:${isRTL ? 'right' : 'left'};padding-${isRTL ? 'right' : 'left'}:12px !important;"><b>${isRTL ? 'الرصيد الافتتاحي' : 'Opening Balance'}</b></td>
            <td></td><td></td>
            <td class="cell-amt ${data.openingBalance < 0 ? 'cell-credit' : 'cell-debit'}"><b>${fmt(Math.abs(data.openingBalance))} ${data.openingBalance < 0 ? (isRTL ? 'دائن' : 'Cr') : (isRTL ? 'مدين' : 'Dr')}</b></td>
          </tr>`;
        const rows = data.details.map((d, i) => {
          runningBalance += (d.debit || 0) - (d.credit || 0);
          return `
            <tr class="${i % 2 === 0 ? 'even' : 'odd'}">
              <td class="cell-num">${i + 1}</td>
              <td class="cell-date">${fmtDate(d.date)}</td>
              <td class="cell-code">${d.entryNo}</td>
              <td class="cell-desc">${(d.description || '').replace(/</g, '&lt;')}</td>
              <td class="cell-amt cell-debit">${d.debit > 0 ? fmt(d.debit) : ''}</td>
              <td class="cell-amt cell-credit">${d.credit > 0 ? fmt(d.credit) : ''}</td>
              <td class="cell-amt ${runningBalance < 0 ? 'cell-credit' : 'cell-debit'}"><b>${fmt(Math.abs(runningBalance))}</b></td>
            </tr>`;
        }).join('');
        bodyRows = openingRow + rows;
        totalsRow = `
          <tr class="totals-row">
            <td colspan="4" style="text-align:${isRTL ? 'right' : 'left'};padding-${isRTL ? 'right' : 'left'}:12px !important;"><b>${isRTL ? `الإجمالي (${data.details.length} حركة)` : `TOTAL (${data.details.length} entries)`}</b></td>
            <td class="cell-amt cell-debit"><b>${fmt(data.totalDebit)}</b></td>
            <td class="cell-amt cell-credit"><b>${fmt(data.totalCredit)}</b></td>
            <td class="cell-amt ${balanceClass}"><b>${fmt(Math.abs(data.closingBalance))} ${isCredit ? (isRTL ? 'دائن' : 'Cr') : (isRTL ? 'مدين' : 'Dr')}</b></td>
          </tr>`;
      } else {
        // SUMMARY: group by month
        const groups = new Map<string, { debit: number; credit: number; count: number }>();
        data.details.forEach(d => {
          const dt = new Date(d.date);
          const key = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`;
          const g = groups.get(key) || { debit: 0, credit: 0, count: 0 };
          g.debit += d.debit || 0;
          g.credit += d.credit || 0;
          g.count++;
          groups.set(key, g);
        });
        let running = data.openingBalance;
        const sortedKeys = Array.from(groups.keys()).sort();
        const openingRow = `
          <tr class="opening-row">
            <td colspan="2" style="text-align:${isRTL ? 'right' : 'left'};padding-${isRTL ? 'right' : 'left'}:12px !important;"><b>${isRTL ? 'الرصيد الافتتاحي' : 'Opening Balance'}</b></td>
            <td></td><td></td>
            <td class="cell-amt ${data.openingBalance < 0 ? 'cell-credit' : 'cell-debit'}"><b>${fmt(Math.abs(data.openingBalance))} ${data.openingBalance < 0 ? (isRTL ? 'دائن' : 'Cr') : (isRTL ? 'مدين' : 'Dr')}</b></td>
          </tr>`;
        const monthNames = isRTL
          ? ['يناير','فبراير','مارس','أبريل','مايو','يونيو','يوليو','أغسطس','سبتمبر','أكتوبر','نوفمبر','ديسمبر']
          : ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
        const rows = sortedKeys.map((key, i) => {
          const g = groups.get(key)!;
          running += g.debit - g.credit;
          const [y, m] = key.split('-');
          const monthLabel = `${monthNames[parseInt(m) - 1]} ${y}`;
          return `
            <tr class="${i % 2 === 0 ? 'even' : 'odd'}">
              <td class="cell-num">${i + 1}</td>
              <td class="cell-date"><b>${monthLabel}</b></td>
              <td class="cell-num">${g.count}</td>
              <td class="cell-amt cell-debit">${fmt(g.debit)}</td>
              <td class="cell-amt cell-credit">${fmt(g.credit)}</td>
              <td class="cell-amt ${running < 0 ? 'cell-credit' : 'cell-debit'}"><b>${fmt(Math.abs(running))}</b></td>
            </tr>`;
        }).join('');
        bodyRows = openingRow + rows;
        totalsRow = `
          <tr class="totals-row">
            <td colspan="3" style="text-align:${isRTL ? 'right' : 'left'};padding-${isRTL ? 'right' : 'left'}:12px !important;"><b>${isRTL ? `الإجمالي (${groups.size} شهر، ${data.details.length} حركة)` : `TOTAL (${groups.size} months, ${data.details.length} entries)`}</b></td>
            <td class="cell-amt cell-debit"><b>${fmt(data.totalDebit)}</b></td>
            <td class="cell-amt cell-credit"><b>${fmt(data.totalCredit)}</b></td>
            <td class="cell-amt ${balanceClass}"><b>${fmt(Math.abs(data.closingBalance))} ${isCredit ? (isRTL ? 'دائن' : 'Cr') : (isRTL ? 'مدين' : 'Dr')}</b></td>
          </tr>`;
      }

      const tableHead = type === 'detailed' ? `
        <thead>
          <tr>
            <th>#</th>
            <th>${isRTL ? 'التاريخ' : 'Date'}</th>
            <th>${isRTL ? 'رقم القيد' : 'Entry No.'}</th>
            <th>${isRTL ? 'البيان' : 'Description'}</th>
            <th>${isRTL ? 'مدين' : 'Debit'}</th>
            <th>${isRTL ? 'دائن' : 'Credit'}</th>
            <th>${isRTL ? 'الرصيد' : 'Balance'}</th>
          </tr>
        </thead>` : `
        <thead>
          <tr>
            <th>#</th>
            <th>${isRTL ? 'الشهر' : 'Month'}</th>
            <th>${isRTL ? 'عدد الحركات' : 'Entries'}</th>
            <th>${isRTL ? 'مدين' : 'Debit'}</th>
            <th>${isRTL ? 'دائن' : 'Credit'}</th>
            <th>${isRTL ? 'الرصيد المتحرك' : 'Running Balance'}</th>
          </tr>
        </thead>`;

      const docTitle = type === 'detailed'
        ? (isRTL ? 'كشف حساب تفصيلي' : 'Detailed Account Statement')
        : (isRTL ? 'كشف حساب مختصر' : 'Summary Account Statement');

      const accountDisplay = isRTL
        ? (data.accountNameAr || data.accountName)
        : data.accountName;

      const html = `<!DOCTYPE html>
<html dir="${isRTL ? 'rtl' : 'ltr'}">
<head>
<meta charset="UTF-8">
<title>${data.accountCode} - ${accountDisplay}</title>
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: 'Segoe UI', Tahoma, Arial, sans-serif; padding: 18px; direction: ${isRTL ? 'rtl' : 'ltr'}; color: #1a1a1a; background: #fff; }
  .header { text-align: center; border-bottom: 3px solid #00796B; padding-bottom: 14px; margin-bottom: 14px; }
  .header img { max-height: 60px; margin-bottom: 6px; }
  .header h2 { color: #00796B; margin: 4px 0; font-size: 20px; }
  .header .subtitle { font-size: 11px; color: #555; }
  table.title-bar { width: 100%; border-collapse: collapse; background: linear-gradient(135deg, #00796B 0%, #00ACC1 100%); color: #fff; border-radius: 8px; margin-bottom: 14px; overflow: hidden; }
  table.title-bar td { padding: 12px 20px; vertical-align: middle; }
  table.title-bar h3 { font-size: 18px; margin: 0; }
  table.title-bar .meta { font-size: 12px; opacity: 0.95; }
  table.stats-tbl { width: 100%; border-collapse: separate; border-spacing: 6px 0; margin-bottom: 14px; }
  table.stats-tbl td { width: 25%; padding: 0; vertical-align: top; }
  .stat-card { padding: 10px 12px; border-radius: 8px; color: #fff; text-align: center; box-shadow: 0 2px 6px rgba(0,0,0,0.08); }
  .stat-card .label { font-size: 10px; opacity: 0.95; margin-bottom: 4px; }
  .stat-card .val { font-size: 15px; font-weight: bold; }
  .s-open { background: linear-gradient(135deg, #5E35B1, #9575CD); }
  .s-debit { background: linear-gradient(135deg, #2E7D32, #66BB6A); }
  .s-credit { background: linear-gradient(135deg, #C62828, #EF5350); }
  .s-close { background: linear-gradient(135deg, #1565C0, #42A5F5); }
  .account-card { border: 2px solid #00796B; border-radius: 8px; padding: 10px 14px; margin-bottom: 12px; background: #F1F8F8; }
  .account-card .ac-code { color: #00796B; font-weight: bold; font-size: 16px; }
  .account-card .ac-name { font-size: 14px; color: #333; margin-${isRTL ? 'right' : 'left'}: 10px; }
  table.main { width: 100%; border-collapse: collapse; font-size: 11px; }
  table.main thead th { background: #00796B; color: #fff; padding: 8px 6px; text-align: center; font-weight: 600; border: 1px solid #00564E; font-size: 11px; }
  table.main tbody td { padding: 6px 8px; border: 1px solid #DDD; text-align: center; }
  table.main tbody tr.odd td { background: #FAFAFA; }
  table.main tbody tr.even td { background: #fff; }
  table.main tbody tr.opening-row td { background: linear-gradient(135deg, #EDE7F6, #D1C4E9) !important; }
  .cell-num { color: #5E35B1; font-weight: 600; }
  .cell-date { color: #1565C0; font-weight: 600; white-space: nowrap; }
  .cell-code { color: #00796B; font-weight: bold; font-family: monospace; }
  .cell-desc { text-align: ${isRTL ? 'right' : 'left'} !important; padding-${isRTL ? 'right' : 'left'}: 10px !important; }
  .cell-amt { text-align: ${isRTL ? 'left' : 'right'} !important; padding-${isRTL ? 'left' : 'right'}: 10px !important; font-variant-numeric: tabular-nums; font-weight: 600; }
  .cell-debit { color: #2E7D32; }
  .cell-credit { color: #C62828; }
  .bal-debit { color: #2E7D32; }
  .bal-credit { color: #C62828; }
  tr.totals-row td { background: linear-gradient(135deg, #00796B, #00ACC1) !important; color: #fff !important; font-weight: bold; padding: 10px 8px !important; font-size: 12px !important; border: 1px solid #00564E !important; }
  tr.totals-row td.cell-debit, tr.totals-row td.cell-credit { color: #fff !important; }
  .footer { margin-top: 28px; display: flex; justify-content: space-between; gap: 30px; }
  .sig { text-align: center; flex: 1; }
  .sig .line { border-top: 1.5px solid #333; margin-top: 50px; padding-top: 6px; font-weight: bold; font-size: 11px; color: #333; }
  .footer-meta { margin-top: 14px; text-align: center; font-size: 9px; color: #999; }
  @media print {
    @page { size: A4; margin: 10mm; }
    body { padding: 0; }
    table.main tbody tr { page-break-inside: avoid; }
    table.main thead { display: table-header-group; }
    .footer { page-break-inside: avoid; }
  }
</style>
</head>
<body>

${companyInfo ? `
<div class="header">
  ${companyInfo.logoPath ? `<img src="${config.API_ORIGIN}${companyInfo.logoPath}" onerror="this.style.display='none'" />` : ''}
  <h2>${isRTL ? companyInfo.companyNameAr : companyInfo.companyName}</h2>
  <div class="subtitle">
    ${companyInfo.address ? companyInfo.address + ' | ' : ''}${companyInfo.phone ? '📞 ' + companyInfo.phone + ' | ' : ''}${companyInfo.email ? '✉️ ' + companyInfo.email : ''}${companyInfo.website ? ' | 🌐 ' + companyInfo.website : ''}
  </div>
</div>` : ''}

<table class="title-bar"><tr>
  <td style="text-align:${isRTL ? 'right' : 'left'};"><h3>📋 ${docTitle}</h3></td>
  <td style="text-align:${isRTL ? 'left' : 'right'};"><div class="meta"><strong>${isRTL ? 'الفترة' : 'Period'}: ${periodStr}</strong></div></td>
</tr></table>

<div class="account-card">
  <span class="ac-code">${data.accountCode}</span>
  <span class="ac-name">— ${accountDisplay}</span>
</div>

<table class="stats-tbl"><tr>
  <td><div class="stat-card s-open"><div class="label">${isRTL ? 'الرصيد الافتتاحي' : 'Opening Balance'}</div><div class="val">${fmt(Math.abs(data.openingBalance))} ${data.openingBalance < 0 ? (isRTL ? 'دائن' : 'Cr') : (isRTL ? 'مدين' : 'Dr')}</div></div></td>
  <td><div class="stat-card s-debit"><div class="label">${isRTL ? 'إجمالي المدين' : 'Total Debit'}</div><div class="val">${fmt(data.totalDebit)}</div></div></td>
  <td><div class="stat-card s-credit"><div class="label">${isRTL ? 'إجمالي الدائن' : 'Total Credit'}</div><div class="val">${fmt(data.totalCredit)}</div></div></td>
  <td><div class="stat-card s-close"><div class="label">${isRTL ? 'الرصيد الختامي' : 'Closing Balance'}</div><div class="val">${fmt(Math.abs(data.closingBalance))} ${isCredit ? (isRTL ? 'دائن' : 'Cr') : (isRTL ? 'مدين' : 'Dr')}</div></div></td>
</tr></table>

<table class="main">
  ${tableHead}
  <tbody>
    ${bodyRows}
    ${totalsRow}
  </tbody>
</table>

<div class="footer">
  <div class="sig"><div class="line">${isRTL ? 'المحاسب' : 'Accountant'}</div></div>
  <div class="sig"><div class="line">${isRTL ? 'المدير المالي' : 'Financial Manager'}</div></div>
  <div class="sig"><div class="line">${isRTL ? 'المدير العام' : 'General Manager'}</div></div>
</div>

<div class="footer-meta">
  ${isRTL ? 'تم الإنشاء في' : 'Generated'}: ${new Date().toLocaleString(isRTL ? 'ar-KW' : 'en-GB')}
</div>

</body>
</html>`;

      pw.document.write(html);
      pw.document.close();
      pw.focus();
      setTimeout(() => pw.print(), 600);
    };

    return (
      <>
        <Menu
          open={menuAnchor !== null}
          onClose={() => setMenuAnchor(null)}
          anchorReference="anchorPosition"
          anchorPosition={menuAnchor !== null ? { top: menuAnchor.mouseY, left: menuAnchor.mouseX } : undefined}
        >
          <MenuItem disabled sx={{ opacity: '1 !important', borderBottom: '1px solid #ddd', mb: 1 }}>
            <ListItemIcon><AccountIcon sx={{ color: '#00796B' }} /></ListItemIcon>
            <ListItemText
              primary={<Typography variant="caption" sx={{ color: '#00796B', fontWeight: 'bold' }}>{account?.accountCode}</Typography>}
              secondary={isRTL ? (account?.accountNameAr || account?.accountName) : account?.accountName}
            />
          </MenuItem>
          <MenuItem onClick={() => handleMenuSelect('detailed')}>
            <ListItemIcon><DetailedIcon sx={{ color: '#1565C0' }} /></ListItemIcon>
            <ListItemText>{t.detailed}</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => handleMenuSelect('summary')}>
            <ListItemIcon><SummaryIcon sx={{ color: '#5E35B1' }} /></ListItemIcon>
            <ListItemText>{t.summary}</ListItemText>
          </MenuItem>
        </Menu>

        <Dialog open={dialogOpen} onClose={handleClose} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ background: 'linear-gradient(135deg, #00796B, #00ACC1)', color: '#fff', display: 'flex', alignItems: 'center', gap: 1 }}>
            <AccountIcon /> {t.dialogTitle}
          </DialogTitle>
          <DialogContent dividers>
            {account && (
              <Box sx={{ mb: 2, p: 1.5, border: '2px solid #00796B', borderRadius: 1, bgcolor: 'rgba(0,121,107,0.05)' }}>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>{t.account}</Typography>
                <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#00796B' }}>
                  {account.accountCode} — {isRTL ? (account.accountNameAr || account.accountName) : account.accountName}
                </Typography>
              </Box>
            )}

            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <DatePicker
                  label={t.from}
                  value={fromDate}
                  onChange={setFromDate}
                  format="dd/MM/yyyy"
                  minDate={minDate}
                  maxDate={maxDate}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
                <DatePicker
                  label={t.to}
                  value={toDate}
                  onChange={setToDate}
                  format="dd/MM/yyyy"
                  minDate={minDate}
                  maxDate={maxDate}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
              </Box>
            </LocalizationProvider>

            <Divider sx={{ my: 2 }} />
            <Typography variant="caption" sx={{ color: 'text.secondary', mb: 1, display: 'block' }}>{t.type}</Typography>
            <ToggleButtonGroup
              value={reportType}
              exclusive
              onChange={(_, v) => v && setReportType(v)}
              fullWidth
              sx={{ '& .MuiToggleButton-root.Mui-selected': { background: 'linear-gradient(135deg, #00796B, #00ACC1)', color: '#fff' } }}
            >
              <ToggleButton value="detailed">
                <DetailedIcon sx={{ mr: 1 }} /> {t.detailed}
              </ToggleButton>
              <ToggleButton value="summary">
                <SummaryIcon sx={{ mr: 1 }} /> {t.summary}
              </ToggleButton>
            </ToggleButtonGroup>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} startIcon={<CloseIcon />}>{t.cancel}</Button>
            <Button
              variant="contained"
              startIcon={loading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : <PrintIcon />}
              onClick={handlePrint}
              disabled={loading || !fromDate || !toDate}
              sx={{ background: 'linear-gradient(135deg, #00796B, #00ACC1)' }}
            >
              {loading ? t.loading : t.print}
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }
);

export default AccountStatementContextMenu;
